import type { DisplayTemplate, DisplayTemplateAst, RefNode } from '#layers/data-model/types/display-template';
import type { M2MStructure } from '#layers/data-model/types/relation';
import type { GlobalFieldId } from '#layers/data-model/types/schema';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import { isNil } from '#layers/base';
import { isNullishOrEmpty } from '#layers/base/utils/iterable';
import { parseBlockExpression } from '#layers/block-engine';
import { getCollectionPrimaryKeyFields } from '#layers/data-model/utils/schema/collection';
import { isPrimitiveTypeString } from '#layers/data-model/utils/schema/primitive-type';

export function getCollectionDefaultDisplayTemplate(collection: ResolvedMonospaceCollection): DisplayTemplateAst | null {
	const titleField = collection.primitiveFields.find((field) => isPrimitiveTypeString(field.type)) ?? getCollectionPrimaryKeyFields(collection)[0];

	if (titleField) {
		return fieldIdToDisplayTemplateAst(titleField.id);
	}

	return null;
}

/** The programmatic equivalent of typing `{{ fieldName }}` in the template editor. */
export function fieldIdToDisplayTemplateAst(fieldId: GlobalFieldId): DisplayTemplateAst {
	return fieldPathToDisplayTemplateAst([fieldId] as CollectionFieldPath);
}

/**
 * The default display template for an M2M relation: `{{ <junction-to-target field> }}`.
 *
 * Rendered against the JUNCTION row, so junction-local fields stay in scope for future
 * per-field templates; the default just surfaces the target. The target leg is field-relative
 * (it flips by viewing side), so this is derived from the resolved structure, never a static
 * junction-collection template.
 */
export function getM2MDefaultDisplayTemplate(structure: M2MStructure): DisplayTemplateAst {
	return fieldIdToDisplayTemplateAst(structure.junction.toTarget.id);
}

/** The programmatic equivalent of selecting a field path in the template editor. */
export function fieldPathToDisplayTemplateAst(fieldPath: CollectionFieldPath): DisplayTemplateAst {
	return {
		type: 'doc',
		content: [fieldPathToDisplayTemplateRefNode(fieldPath)],
	};
}

export function fieldPathToDisplayTemplateRefNode(fieldPath: CollectionFieldPath): RefNode {
	return {
		type: 'ref',
		attrs: {
			fieldId: fieldPath.length === 1 ? fieldPath[0]! : fieldPath,
		},
	};
}

export function isDisplayTemplateString(displayTemplate: DisplayTemplate): displayTemplate is string {
	return typeof displayTemplate === 'string';
}

export function isDisplayTemplateAst(displayTemplate: DisplayTemplate): displayTemplate is DisplayTemplateAst {
	return typeof displayTemplate === 'object' && 'type' in displayTemplate;
}

export function isEmptyDisplayTemplate(displayTemplate?: DisplayTemplate | null): displayTemplate is null | undefined {
	if (isNil(displayTemplate)) return true;
	if (isDisplayTemplateString(displayTemplate)) return displayTemplate.trim().length === 0;
	return isNullishOrEmpty(displayTemplate.content);
}

export function toDisplayTemplateAst(
	template: DisplayTemplate | null | undefined,
	collection: ResolvedMonospaceCollection,
): DisplayTemplateAst {
	const empty: DisplayTemplateAst = { type: 'doc', content: [] };

	if (isNil(template)) return empty;
	if (isDisplayTemplateAst(template)) return template;
	if (typeof template !== 'string' || template.trim().length === 0) return empty;

	let parsed: ReturnType<typeof parseBlockExpression>;

	try {
		parsed = parseBlockExpression(template);
	}
	catch {
		return empty;
	}

	const content: DisplayTemplateAst['content'] = [];

	for (const node of parsed) {
		if (node.type === 'static') {
			const text = String(node.value);
			if (text.length > 0) content.push({ type: 'text', text });
		}
		else if (node.type === 'expression') {
			const { child } = node;

			if (child.type === 'prop' && child.path.length > 0) {
				const fieldIds = resolveFieldPath(child.path, collection);

				if (fieldIds) {
					content.push({
						type: 'ref',
						attrs: {
							fieldId: fieldIds.length === 1 ? fieldIds[0]! : fieldIds,
						},
					});
				}
			}
			else if (child.type === 'static' && child.value != null) {
				content.push({ type: 'text', text: String(child.value) });
			}
		}
	}

	return { type: 'doc', content };
}

function resolveFieldPath(path: string[], collection: ResolvedMonospaceCollection): GlobalFieldId[] | null {
	if (path.length === 0) return null;

	const [segment, ...rest] = path;
	const allFields = [...collection.primitiveFields, ...collection.relationFields];
	const field = allFields.find((f) => f.id === segment || f.apiName === segment);

	if (!field) return null;

	if (rest.length === 0) return [field.id as GlobalFieldId];

	const relationField = collection.relationFields.find((f) => f.id === field.id);
	if (!relationField?.oppositeCollection) return null;

	const nested = resolveFieldPath(rest, relationField.oppositeCollection as ResolvedMonospaceCollection);
	if (!nested) return null;

	return [field.id as GlobalFieldId, ...nested];
}
